import { HttpError } from 'react-admin';

import {
  getEnv,
} from '@src/apis/shared';
import type { ExecRequestBody } from '@src/types';


/**
 * Executes a typed GraphQL query with standard headers for the partner dashboard app.
 * @param query - The GraphQL query or mutation string.
 * @param body - Object containing variables for the GraphQL query.
 * @returns A promise resolving with the typed response data.
 */
export const exec = async <TVariables, TResponse>(
  query: string,
  body: ExecRequestBody<TVariables>
): Promise<TResponse> => {
  const nameMatch = query.trim().match('^([ a-zA-Z]*)') ?? [];
  const name = nameMatch?.length > 1 ? nameMatch[1].replace(' ', '-') : 'undefined';

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-trove-app-name': 'partner-dashboard',
  };

  const response = await fetch(`${getEnv('REWARE_API_BASE')}/graphql?q=${name}`, {
    mode: 'cors',
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables: (body.variables || {}) }),
  });

  if (!response.ok) {
    throw new HttpError(
      // statusText is empty over HTTP/2, which would otherwise surface as a blank error toast.
      response.statusText || `Request failed with status ${response.status}`,
      response.status
    );
  }

  const result = await response.json();

  if (result.errors?.[0]?.message) {
    // Attach the full flat GraphQL error object (message/code/subcode/... at the
    // root, no `extensions` wrapper) as the HttpError body so callers can
    // inspect `body.subcode` for resource-specific error handling
    // (e.g. DATA_NOT_FOUND).
    throw new HttpError(result.errors[0].message, 400, result.errors[0]);
  }

  return result.data;
};

/**
 * Executes a typed GraphQL query to the admin_graphql endpoint with JWT authentication.
 * Requires a JWT token in sessionStorage for authorization.
 * @param query - The GraphQL query or mutation string.
 * @param body - Object containing variables for the GraphQL query.
 * @returns A promise resolving with the typed response data.
 * @throws HttpError if the JWT token is missing or the request fails.
 */
export const execAdmin = async <TVariables, TResponse>(
  query: string,
  body: ExecRequestBody<TVariables>
): Promise<TResponse> => {
  const nameMatch = query.trim().match('^([ a-zA-Z]*)') ?? [];
  const name = nameMatch?.length > 1 ? nameMatch[1].replace(' ', '-') : 'undefined';

  // Get JWT token from sessionStorage
  const token = sessionStorage.getItem('token');
  if (!token) {
    throw new HttpError('Authentication required: JWT token not found', 401);
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-trove-app-name': 'partner-dashboard',
    Authorization: `Bearer ${token}`,
  };

  const response = await fetch(`${getEnv('REWARE_API_BASE')}/admin_graphql?q=${name}`, {
    mode: 'cors',
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables: (body.variables || {}) }),
  });

  if (!response.ok) {
    throw new HttpError(
      // statusText is empty over HTTP/2, which would otherwise surface as a blank error toast.
      response.statusText || `Request failed with status ${response.status}`,
      response.status
    );
  }

  const result = await response.json();

  if (result.errors?.[0]?.message) {
    // Attach the full flat GraphQL error object (message/code/subcode/... at the
    // root, no `extensions` wrapper) as the HttpError body so callers can
    // inspect `body.subcode` for resource-specific error handling
    // (e.g. DATA_NOT_FOUND).
    throw new HttpError(result.errors[0].message, 400, result.errors[0]);
  }

  return result.data;
};

/**
 * Executes a GraphQL mutation to the admin_graphql endpoint with multipart/form-data
 * support for file uploads. Combines execAdmin's JWT authentication with execForm's
 * file upload handling.
 * @param query - The GraphQL mutation string.
 * @param variables - Variables for the GraphQL mutation.
 * @param files - Object containing files to upload, keyed by field name.
 * @returns A promise resolving with the typed response data.
 * @throws HttpError if the JWT token is missing or the request fails.
 */
export const execAdminForm = async <TVariables, TResponse>(
  query: string,
  variables: TVariables,
  files: { [key: string]: File }
): Promise<TResponse> => {
  const nameMatch = query.trim().match('^([ a-zA-Z]*)') ?? [];
  const name = nameMatch?.length > 1 ? nameMatch[1].replace(' ', '-') : 'undefined';

  const token = sessionStorage.getItem('token');
  if (!token) {
    throw new HttpError('Authentication required: JWT token not found', 401);
  }

  const formData = new FormData();

  if (files && Object.keys(files).length > 0) {
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });
  }

  formData.append('query', query);
  formData.append('variables', JSON.stringify(variables || {}));

  const headers: HeadersInit = {
    // Note: Do NOT set Content-Type for FormData - browser will set it with boundary
    'x-trove-app-name': 'partner-dashboard',
    Authorization: `Bearer ${token}`,
  };

  const response = await fetch(`${getEnv('REWARE_API_BASE')}/admin_graphql?q=${name}`, {
    mode: 'cors',
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new HttpError(
      // statusText is empty over HTTP/2, which would otherwise surface as a blank error toast.
      response.statusText || `Request failed with status ${response.status}`,
      response.status
    );
  }

  const result = await response.json();

  if (result.errors?.[0]?.message) {
    // Attach the full flat GraphQL error object (message/code/subcode/... at the
    // root, no `extensions` wrapper) as the HttpError body so callers can
    // inspect `body.subcode` for resource-specific error handling
    // (e.g. DATA_NOT_FOUND).
    throw new HttpError(result.errors[0].message, 400, result.errors[0]);
  }

  return result.data;
};

/**
 * Executes a GraphQL mutation with multipart/form-data support for file uploads.
 * Sends files along with GraphQL query and variables in a single request.
 * @param query - The GraphQL mutation string.
 * @param variables - Variables for the GraphQL mutation.
 * @param files - Object containing files to upload, keyed by field name.
 * @returns A promise resolving with the typed response data.
 */
export const execForm = async <TVariables, TResponse>(
  query: string,
  variables: TVariables,
  files: { [key: string]: File }
): Promise<TResponse> => {
  const nameMatch = query.trim().match('^([ a-zA-Z]*)') ?? [];
  const name = nameMatch?.length > 1 ? nameMatch[1].replace(' ', '-') : 'undefined';

  const formData = new FormData();

  // Append files first
  if (files && Object.keys(files).length > 0) {
    console.debug(`Uploading files with keys: ${Object.keys(files).join(', ')}`);
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });
  }

  // Append GraphQL query and variables
  formData.append('query', query);
  formData.append('variables', JSON.stringify(variables || {}));

  const headers: HeadersInit = {
    // Note: Do NOT set Content-Type for FormData - browser will set it with boundary
    'x-trove-app-name': 'partner-dashboard',
  };

  const response = await fetch(`${getEnv('REWARE_API_BASE')}/graphql?q=${name}`, {
    mode: 'cors',
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new HttpError(
      // statusText is empty over HTTP/2, which would otherwise surface as a blank error toast.
      response.statusText || `Request failed with status ${response.status}`,
      response.status
    );
  }

  const result = await response.json();

  if (result.errors?.[0]?.message) {
    // Attach the full flat GraphQL error object (message/code/subcode/... at the
    // root, no `extensions` wrapper) as the HttpError body so callers can
    // inspect `body.subcode` for resource-specific error handling
    // (e.g. DATA_NOT_FOUND).
    throw new HttpError(result.errors[0].message, 400, result.errors[0]);
  }

  return result.data;
};
